"use client";

/**
 * Responsive Popover Component
 *
 * Uses the Standardized Drawer System for mobile popover content.
 * For comprehensive drawer usage guide, examples, and best practices, see:
 * 📖 docs/ui-components/STANDARDIZED_DRAWER_README.md
 */

import * as React from "react";
import { useMediaQuery } from "@/lib/hooks/ui/use-media-query";
import { DesktopPopover } from "./DesktopPopover";
import { MobileDrawer } from "./MobileDrawer";
import { useResponsivePopoverDrag } from "./use-drag";
import type { ResponsivePopoverProps } from "./types";

export const ResponsivePopover: React.FC<ResponsivePopoverProps> = ({
  children,
  content,
  title,
  open,
  onOpenChange,
  className = "",
  align = "center",
  popoverSide = "bottom",
  drawerSide = "bottom",
  mobileBreakpoint = "(max-width: 768px)",
  forcePopover = false,
  forceDrawer = false,
  asChild = false,
  draggable = false,
  showCloseButton = true,
  stickySubmit,
  useDrawerList = false,
  searchable = false,
  searchPlaceholder = "Search...",
  searchValue,
  onSearchChange,
  emptyMessage = "No items found",
  offset = 8,
  withinPortal = true,
  centerOnScreen = false,
}) => {
  const isMobile = useMediaQuery(mobileBreakpoint);

  // Also check for touch devices and iOS for better mobile detection
  // Preserved from original: hook order must remain identical even though
  // these values are not currently read in the render path.
  const isTouchDevice = React.useMemo(() => {
    if (typeof window === "undefined") return false;
    return "ontouchstart" in window || navigator.maxTouchPoints > 0;
  }, []);

  const isIOS = React.useMemo(() => {
    if (typeof window === "undefined") return false;
    return /iPad|iPhone|iPod/.test(navigator.userAgent);
  }, []);

  // Determine whether to use drawer or popover - ONLY on mobile
  const shouldUseDrawer = React.useMemo(() => {
    if (forcePopover) return false;
    if (forceDrawer) return true;
    // Only use drawer on mobile, not desktop (even with touch)
    return isMobile;
  }, [isMobile, forcePopover, forceDrawer]);

  // Drag functionality (hooks must run unconditionally to preserve hook order)
  const drag = useResponsivePopoverDrag({ draggable, open });

  if (shouldUseDrawer) {
    return (
      <MobileDrawer
        title={title}
        className={className}
        drawerSide={drawerSide}
        showCloseButton={showCloseButton}
        stickySubmit={stickySubmit}
        asChild={asChild}
        onOpenChange={onOpenChange}
        content={content}
      >
        {children}
      </MobileDrawer>
    );
  }

  return (
    <DesktopPopover
      open={open}
      onOpenChange={onOpenChange}
      className={className}
      align={align}
      popoverSide={popoverSide}
      draggable={draggable}
      showCloseButton={showCloseButton}
      searchable={searchable}
      searchPlaceholder={searchPlaceholder}
      searchValue={searchValue}
      onSearchChange={onSearchChange}
      offset={offset}
      withinPortal={withinPortal}
      drag={drag}
      content={content}
    >
      {children}
    </DesktopPopover>
  );
};
